import React from 'react';
import { ArrowRightIcon, UsersIcon, MessageCircleIcon, PresentationIcon, ZapIcon, BookOpenIcon, LayersIcon, MicIcon, UserIcon, GraduationCapIcon, BuildingIcon } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
interface ChallengeCard {
  icon: React.ReactNode;
  accent: string;
  accentLight: string;
  accentText: string;
  borderColor: string;
  titleKey: string;
  subtitleKey: string;
  descKey: string;
  path: string;
}
interface WayCard {
  icon: React.ReactNode;
  lightColor: string;
  textColor: string;
  borderColor: string;
  titleKey: string;
  descKey: string;
  path: string;
}
export function Services() {
  const {
    t
  } = useLanguage();
  const ways: WayCard[] = [{
    icon: <UserIcon size={28} data-id="element-1199" />,
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    titleKey: 'home.ways.coaching.title',
    descKey: 'home.ways.coaching.desc',
    path: '/services/coaching'
  }, {
    icon: <LayersIcon size={28} data-id="element-1200" />,
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    titleKey: 'home.ways.facilitation.title',
    descKey: 'home.ways.facilitation.desc',
    path: '/services/meetings-and-facilitation'
  }, {
    icon: <GraduationCapIcon size={28} data-id="element-1201" />,
    lightColor: 'bg-yellow-100',
    textColor: 'text-yellow-600',
    borderColor: 'border-yellow-200',
    titleKey: 'home.ways.training.title',
    descKey: 'home.ways.training.desc',
    path: '/services/courses'
  }, {
    icon: <BuildingIcon size={28} data-id="element-1202" />,
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    titleKey: 'home.ways.embedded.title',
    descKey: 'home.ways.embedded.desc',
    path: '/services/embedded-partner'
  }];
  const challenges: ChallengeCard[] = [{
    icon: <UsersIcon size={32} data-id="element-1203" />,
    accent: 'bg-coral',
    accentLight: 'bg-coral/10',
    accentText: 'text-coral',
    borderColor: 'border-coral/20',
    titleKey: 'services.challenge1Title',
    subtitleKey: 'services.challenge1Subtitle',
    descKey: 'services.challenge1Desc',
    path: '/services/new-to-leadership'
  }, {
    icon: <MessageCircleIcon size={32} data-id="element-1204" />,
    accent: 'bg-teal',
    accentLight: 'bg-teal/10',
    accentText: 'text-teal',
    borderColor: 'border-teal/20',
    titleKey: 'services.challenge2Title',
    subtitleKey: 'services.challenge2Subtitle',
    descKey: 'services.challenge2Desc',
    path: '/services/conflict-and-conversations'
  }, {
    icon: <PresentationIcon size={32} data-id="element-1205" />,
    accent: 'bg-yellow-500',
    accentLight: 'bg-yellow-100',
    accentText: 'text-yellow-600',
    borderColor: 'border-yellow-200',
    titleKey: 'services.challenge3Title',
    subtitleKey: 'services.challenge3Subtitle',
    descKey: 'services.challenge3Desc',
    path: '/services/meetings-and-facilitation'
  }, {
    icon: <ZapIcon size={32} data-id="element-1206" />,
    accent: 'bg-blue-600',
    accentLight: 'bg-blue-50',
    accentText: 'text-blue-600',
    borderColor: 'border-blue-200',
    titleKey: 'services.challenge4Title',
    subtitleKey: 'services.challenge4Subtitle',
    descKey: 'services.challenge4Desc',
    path: '/services/leading-in-complexity'
  }];
  const formats = [{
    icon: <BookOpenIcon size={28} data-id="element-1207" />,
    color: 'bg-coral/10',
    iconColor: 'text-coral',
    titleKey: 'services.additional.courses.title',
    descKey: 'services.additional.courses.description',
    link: '/services/courses'
  }, {
    icon: <LayersIcon size={28} data-id="element-1208" />,
    color: 'bg-teal/10',
    iconColor: 'text-teal',
    titleKey: 'services.additional.workshops.title',
    descKey: 'services.additional.workshops.description',
    link: '/services/workshops'
  }, {
    icon: <MicIcon size={28} data-id="element-1209" />,
    color: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    titleKey: 'services.additional.talks.title',
    descKey: 'services.additional.talks.description',
    link: '/services/talks'
  }];
  return <PageTransition data-id="element-1210">
      <div className="pt-12 pb-24" data-id="element-1211">
        {/* Header */}
        <section className="text-center max-w-4xl mx-auto px-4 mb-20 relative" data-id="element-1212">
          <DecorativeShape type="square" color="bg-teal/10" className="top-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-1213" />
          <Animate data-id="element-1214">
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-id="element-1215">
              {t('services.title')}{' '}
              <span className="text-coral" data-id="element-1216">{t('services.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1217">
              {t('services.subtitle')}
            </p>
          </Animate>
        </section>

        {/* Ways of working */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-1218">
          <Animate className="mb-10" data-id="element-1219">
            <h2 className="text-2xl font-bold text-charcoal" data-id="element-1220">
              {t('home.ways.title')}
            </h2>
            <p className="text-gray-500 mt-1" data-id="element-1221">{t('home.ways.subtitle')}</p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1222">
            {ways.map((way, i) => <Animate key={i} delay={i * 0.08} data-id="element-1223">
                <Link to={way.path} className="block group h-full" data-id="element-1224">
                  <Card className={`h-full p-6 border-2 ${way.borderColor} hover:shadow-md transition-all`} hoverEffect data-id="element-1225">
                    <div className="flex items-start gap-4" data-id="element-1226">
                      <div className={`p-3 rounded-2xl ${way.lightColor} flex-shrink-0`} data-id="element-1227">
                        <div className={way.textColor} data-id="element-1228">{way.icon}</div>
                      </div>
                      <div className="flex-1" data-id="element-1229">
                        <h3 className={`text-lg font-bold text-charcoal mb-2 group-hover:${way.textColor} transition-colors`} data-id="element-1230">
                          {t(way.titleKey)}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed mb-3" data-id="element-1231">
                          {t(way.descKey)}
                        </p>
                        <div className={`flex items-center ${way.textColor} text-sm font-bold`} data-id="element-1232">
                          {t('home.ways.learnMore')}
                          <ArrowRightIcon size={14} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1233" />
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              </Animate>)}
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1234">
          <div className="border-t border-gray-100" data-id="element-1235" />
        </div>

        {/* Challenge cards */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-1236">
          <Animate className="mb-10" data-id="element-1237">
            <h2 className="text-2xl font-bold text-charcoal" data-id="element-1238">
              {t('services.challengesTitle')}
            </h2>
            <p className="text-gray-500 mt-1" data-id="element-1239">{t('services.challengesSubtitle')}</p>
          </Animate>

          <div className="space-y-6" data-id="element-1240">
            {challenges.map((c, index) => <Animate key={index} delay={index * 0.08} data-id="element-1241">
                <Link to={c.path} className="block group" data-id="element-1242">
                  <Card className={`p-0 overflow-hidden border-2 ${c.borderColor} hover:shadow-lg transition-shadow`} data-id="element-1243">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-0" data-id="element-1244">
                      <div className={`md:col-span-2 ${c.accentLight} p-8 flex flex-col justify-center items-center text-center`} data-id="element-1245">
                        <div className={`w-16 h-16 rounded-full ${c.accent} text-white flex items-center justify-center shadow-md mb-3`} data-id="element-1246">
                          {c.icon}
                        </div>
                        <p className={`text-sm font-bold ${c.accentText} uppercase tracking-wide`} data-id="element-1247">
                          {t(c.subtitleKey)}
                        </p>
                      </div>
                      <div className="md:col-span-10 p-8 flex items-center justify-between bg-white gap-6" data-id="element-1248">
                        <div className="flex-1" data-id="element-1249">
                          <h3 className={`text-2xl font-bold mb-3 text-charcoal group-hover:${c.accentText} transition-colors`} data-id="element-1250">
                            {t(c.titleKey)}
                          </h3>
                          <p className="text-gray-600 leading-relaxed max-w-2xl" data-id="element-1251">
                            {t(c.descKey)}
                          </p>
                        </div>
                        <div className={`flex-shrink-0 ${c.accentText} group-hover:translate-x-1 transition-transform`} data-id="element-1252">
                          <ArrowRightIcon size={28} data-id="element-1253" />
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              </Animate>)}
          </div>
        </section>

        {/* Formats section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1254">
          <Animate className="mb-10" data-id="element-1255">
            <h2 className="text-2xl font-bold text-charcoal" data-id="element-1256">
              {t('services.formatsTitle')}
            </h2>
            <p className="text-gray-500 mt-1" data-id="element-1257">{t('services.formatsSubtitle')}</p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1258">
            {formats.map((f, i) => <Animate key={i} delay={i * 0.1} data-id="element-1259">
                <Card className="p-8 h-full border-2 border-gray-100 hover:shadow-md transition-shadow" data-id="element-1260">
                  <div className={`p-3 rounded-2xl ${f.color} w-fit mb-4`} data-id="element-1261">
                    <div className={f.iconColor} data-id="element-1262">{f.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-charcoal mb-2" data-id="element-1263">
                    {t(f.titleKey)}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-1264">
                    {t(f.descKey)}
                  </p>
                  <Link to={f.link} data-id="element-1265">
                    <Button variant="outline" rightIcon={<ArrowRightIcon size={16} data-id="element-1267" />} data-id="element-1266">
                      {t('services.seeMore')}
                    </Button>
                  </Link>
                </Card>
              </Animate>)}
          </div>
        </section>
      </div>
    </PageTransition>;
}